import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import { Close as CloseIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { FabrikUser } from '../../../types';
import { useAdminCheckin } from '../hooks/useAdminCheckin';

interface CheckinModalProps {
  open: boolean;
  onClose: () => void;
  user: FabrikUser | null;
  onCheckInSuccess?: () => void;
}

const CheckinModal: React.FC<CheckinModalProps> = ({
  open,
  onClose,
  user,
  onCheckInSuccess,
}) => {
  const [selectedSpaceId, setSelectedSpaceId] = useState<string>('');

  // Use the custom hook for all check-in logic
  const {
    // Check-in state
    isCheckingIn,
    checkInError,
    checkInSuccess,
    // Spaces state
    spaces,
    isLoadingSpaces,
    spacesError,
    // Actions
    checkInUser,
    loadSpaces,
    clearState,
  } = useAdminCheckin({
    onSuccess: (response) => {
      // Call the success callback to refresh user data
      if (onCheckInSuccess) {
        onCheckInSuccess();
      }
      // Modal stays open to display check-in results that admin needs to read
    },
    onError: (error) => {
      // Error is already handled by the hook's state
      console.error('Check-in failed:', error);
    }
  });

  // Load spaces when modal opens
  useEffect(() => {
    if (open) {
      clearState();
      setSelectedSpaceId('');
      loadSpaces();
    }
  }, [open, clearState, loadSpaces]);

  const handleClose = () => {
    setSelectedSpaceId('');
    clearState();
    onClose();
  };

  const handleCheckIn = async () => {
    if (!user || !selectedSpaceId) {
      return;
    }

    await checkInUser(user.id, selectedSpaceId);
  };

  const getUserName = (user: FabrikUser): string => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.display_name || user.email || 'Unknown User';
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="checkin-modal-title"
      aria-describedby="checkin-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '80%', md: '60%', lg: '50%' },
          maxWidth: '600px',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Typography
            id="checkin-modal-title"
            variant="h6"
            component="h2"
            sx={{ fontWeight: 600 }}
          >
            Admin Check-in
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {/* User Info */}
          {user && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Checking in:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {user.image_url ? (
                  <img
                    src={user.image_url}
                    alt={getUserName(user)}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      fontWeight: 600,
                    }}
                  >
                    {getUserName(user).split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </Box>
                )}
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {getUserName(user)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          {/* Success Message */}
          {checkInSuccess && (
            <Box>
              <Alert
                severity="success"
                icon={<CheckCircleIcon />}
                sx={{ mb: 3 }}
              >
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Check-in successful!
                </Typography>
                <Typography variant="body2">
                  {checkInSuccess.message || `User checked in using ${checkInSuccess.quota_type === 'day_pass' ? 'day pass' : 'membership quota'}.`}
                </Typography>
              </Alert>

              {/* Updated Quota Information */}
              <Box sx={{ 
                p: 2, 
                bgcolor: 'grey.50', 
                borderRadius: 1, 
                border: '1px solid',
                borderColor: 'grey.200',
                mb: 3 
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                  Updated Quota Status
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  {/* Membership Days */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      {checkInSuccess.has_membership ? (
                        checkInSuccess.quota_type === 'membership_day' ? 
                        `${(user?.usage_data?.available_days || 0) - 1}` : 
                        `${user?.usage_data?.available_days || 0}`
                      ) : 'No Access'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Days Remaining
                    </Typography>
                  </Box>

                  <Box sx={{ height: '30px', width: '1px', bgcolor: 'primary.300' }} />

                  {/* Day Passes */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" fontWeight="bold" color="secondary.main">
                      {checkInSuccess.quota_type === 'day_pass' ? 
                        Math.max(0, (user?.usage_data?.active_day_passes_count || 0) - 1) : 
                        (user?.usage_data?.active_day_passes_count || 0)
                      }
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Day Passes
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  * Values shown reflect the quota used for this check-in
                </Typography>
              </Box>
            </Box>
          )}

          {/* Error Messages */}
          {(checkInError || spacesError) && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {checkInError || spacesError}
            </Alert>
          )}

          {/* Loading Spaces */}
          {isLoadingSpaces && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100px',
              }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Loading spaces...
              </Typography>
            </Box>
          )}

          {/* Form */}
          {!isLoadingSpaces && !checkInSuccess && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Space Selection */}
              <FormControl fullWidth>
                <InputLabel id="space-select-label">Select Space</InputLabel>
                <Select
                  labelId="space-select-label"
                  value={selectedSpaceId}
                  label="Select Space"
                  onChange={(e) => setSelectedSpaceId(e.target.value)}
                  disabled={isCheckingIn}
                >
                  {spaces.map((space) => (
                    <MenuItem key={space.id} value={space.id}>
                      {space.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Day Pass Alternative Link */}
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Typography 
                  variant="body2" 
                  component="button"
                  disabled={!user?.usage_data?.active_day_passes_count || user.usage_data.active_day_passes_count === 0}
                  onClick={() => {
                    // Only allow click if user has active day passes
                    if (user?.usage_data?.active_day_passes_count && user.usage_data.active_day_passes_count > 0) {
                      // TODO: Open day pass modal instead
                      console.log('Open day pass modal for user:', user?.id);
                    }
                  }}
                  sx={{
                    color: (!user?.usage_data?.active_day_passes_count || user.usage_data.active_day_passes_count === 0) 
                      ? 'text.disabled' 
                      : 'primary.main',
                    textDecoration: (!user?.usage_data?.active_day_passes_count || user.usage_data.active_day_passes_count === 0) 
                      ? 'none' 
                      : 'underline',
                    cursor: (!user?.usage_data?.active_day_passes_count || user.usage_data.active_day_passes_count === 0) 
                      ? 'not-allowed' 
                      : 'pointer',
                    border: 'none',
                    background: 'none',
                    '&:hover': {
                      color: (!user?.usage_data?.active_day_passes_count || user.usage_data.active_day_passes_count === 0) 
                        ? 'text.disabled' 
                        : 'primary.dark',
                    }
                  }}
                >
                  Use a day pass instead{(!user?.usage_data?.active_day_passes_count || user.usage_data.active_day_passes_count === 0) ? ' - no day passes available' : ''}
                </Typography>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  disabled={isCheckingIn}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCheckIn}
                  disabled={isCheckingIn || !selectedSpaceId}
                  startIcon={isCheckingIn ? <CircularProgress size={16} /> : null}
                >
                  {isCheckingIn ? 'Checking In...' : 'Check In'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default CheckinModal;
